// The auras drawn around tokens (issue 1150; design/30 §4): each aura's squares — those within its radius of the
// token's space by the system's diagonal rule, the very squares the server reads for who is inside (`inAura`) — as
// one translucent rectangle a row, under the tokens, at wherever the token stands in this viewer's store; so it moves
// with the token. A player's store holds an aura only on a token they were sent: nothing here can draw one they were not.
// With no distance rule the circle is drawn by the token's centre and its radius in squares.
import { footprintSquares, inAura, type Aura, type Diagonal, type Grid } from '@dragons-table/vtt-shared';
import { boardSquares, spanRects } from './areaGeometry';
import type { Extent } from './scene';

export interface AuraPaint { colour: number; rects: Array<{ x: number; y: number; width: number; height: number }>; circle: { x: number; y: number; r: number } | null }
interface Holder { x: number; y: number; size: { w: number; h: number }; auras?: Array<Pick<Aura, 'id' | 'radius' | 'colour'>> }

export const AURA_DEFAULT_COLOUR = 0xffd700;

export function auraPaints(objects: Iterable<Holder>, grid: Grid, extent: Extent, diagonal: Diagonal | null): AuraPaint[] {
  const out: AuraPaint[] = [];
  const { cols, rows } = boardSquares(grid, extent);
  for (const o of objects) {
    for (const a of o.auras ?? []) {
      const colour = a.colour ? parseInt(a.colour.slice(1), 16) : AURA_DEFAULT_COLOUR;
      if (!diagonal) {
        out.push({ colour, rects: [], circle: { x: o.x, y: o.y, r: (a.radius + Math.max(o.size.w, o.size.h) / 2) * grid.x.basePitch } });
        continue;
      }
      const box = footprintSquares(grid, extent, { x: o.x, y: o.y }, o.size);
      const spans: Array<{ j: number; i0: number; i1: number }> = [];
      for (let j = box.j0 - a.radius; j <= box.j1 + a.radius; j += 1) {
        let run: { j: number; i0: number; i1: number } | null = null;
        for (let i = box.i0 - a.radius; i <= box.i1 + a.radius; i += 1) {
          if (i < 0 || j < 0 || i >= cols || j >= rows || !inAura(box, a.radius, diagonal, i, j)) { run = null; continue; }
          if (run && run.i1 === i - 1) run.i1 = i; else { run = { j, i0: i, i1: i }; spans.push(run); }
        }
      }
      out.push({ colour, rects: spanRects(spans, grid, extent), circle: null });
    }
  }
  return out;
}
